import { supabase } from "./supabaseClient";
import type {
  Institution,
  InstitutionUpdate,
  InstitutionMember,
  InstitutionMemberInsert,
  InstitutionPositionType,
  InstitutionScope,
  MembershipWithPerson,
  AuthorityWithPerson,
} from "@/features/institutions/types";

// ── Shared search types ───────────────────────────────────────────────────────

export type MinisterSearchResult = {
  id: string;
  full_name: string | null;
  adb_code: string;
  profile_picture: string | null;
};

// ── Institutions ──────────────────────────────────────────────────────────────

export async function getInstitutions(): Promise<Institution[]> {
  const { data, error } = await supabase
    .from("institutions")
    .select("*")
    .order("name");
  if (error) throw error;
  return data;
}

export async function getInstitutionBySlug(slug: string): Promise<Institution | null> {
  const { data, error } = await supabase
    .from("institutions")
    .select("*")
    .eq("slug", slug)
    .single();
  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }
  return data;
}

export async function updateInstitution(
  id: string,
  updates: InstitutionUpdate,
  userId: string,
): Promise<Institution> {
  const { data, error } = await supabase
    .from("institutions")
    .update({ ...updates, updated_at: new Date().toISOString(), updated_by: userId })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Position types ────────────────────────────────────────────────────────────

export async function getPositionTypes(
  institutionId: string,
  scope?: "national" | "region" | "district",
): Promise<InstitutionPositionType[]> {
  let query = supabase
    .from("institution_position_types")
    .select("*")
    .eq("institution_id", institutionId)
    .eq("is_active", true)
    .order("sort_order");
  if (scope) query = query.eq("scope", scope);
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function getAllPositionTypes(
  institutionId: string,
): Promise<InstitutionPositionType[]> {
  const { data, error } = await supabase
    .from("institution_position_types")
    .select("*")
    .eq("institution_id", institutionId)
    .order("scope")
    .order("sort_order");
  if (error) throw error;
  return data;
}

export async function createPositionType(
  institutionId: string,
  payload: { name: string; scope: "national" | "region" | "district"; sort_order: number },
  userId: string,
): Promise<InstitutionPositionType> {
  const { data, error } = await supabase
    .from("institution_position_types")
    .insert({ institution_id: institutionId, ...payload, created_by: userId })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updatePositionType(
  id: string,
  updates: Partial<Pick<InstitutionPositionType, "name" | "sort_order" | "is_active">>,
): Promise<InstitutionPositionType> {
  const { data, error } = await supabase
    .from("institution_position_types")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Logo ──────────────────────────────────────────────────────────────────────

export function getInstitutionLogoUrl(logoUrl: string | null): string | null {
  if (!logoUrl) return null;
  if (logoUrl.startsWith("http")) return logoUrl;
  return `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/InstitutionFiles/${logoUrl}`;
}

export async function uploadInstitutionLogo(slug: string, file: File): Promise<string> {
  const ext = file.name.split(".").pop() ?? "jpg";
  const path = `logos/${slug}.${ext}`;
  const { error } = await supabase.storage
    .from("InstitutionFiles")
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw error;
  return path;
}

// ── Institution credential requests ───────────────────────────────────────────

export async function getInstitutionCredentialRequests(institutionId: string) {
  const { data, error } = await supabase
    .from("credential_requests")
    .select(`
      *,
      region:regions(id, name, code),
      district:districts(id, name, code),
      creator:users!created_by(id, full_name),
      minister_count:credential_request_ministers(count)
    `)
    .eq("institution_id", institutionId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data;
}

// ── Memberships ───────────────────────────────────────────────────────────────

export async function getMemberships(
  institutionId: string,
): Promise<MembershipWithPerson[]> {
  const { data, error } = await supabase
    .from("institution_memberships")
    .select(`
      *,
      minister:ministers(id, full_name, adb_code, profile_picture),
      institution_member:institution_members(id, full_name, first_name, last_name, profile_picture)
    `)
    .eq("institution_id", institutionId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data as MembershipWithPerson[];
}

// ── Authority positions ───────────────────────────────────────────────────────

export async function getCurrentAuthorities(
  institutionId: string,
): Promise<AuthorityWithPerson[]> {
  const { data, error } = await supabase
    .from("institution_authority_positions")
    .select(`
      *,
      position_type:institution_position_types(id, name, sort_order, scope),
      minister:ministers(id, full_name, adb_code, profile_picture),
      institution_member:institution_members(id, full_name, first_name, last_name, profile_picture),
      region:regions(id, name, code),
      district:districts(id, name, code)
    `)
    .eq("institution_id", institutionId)
    .is("ended_at", null)
    .order("scope")
    .order("created_at");
  if (error) throw error;
  return data as AuthorityWithPerson[];
}

export async function getAuthorityHistory(
  institutionId: string,
): Promise<AuthorityWithPerson[]> {
  const { data, error } = await supabase
    .from("institution_authority_positions")
    .select(`
      *,
      position_type:institution_position_types(id, name, sort_order, scope),
      minister:ministers(id, full_name, adb_code, profile_picture),
      institution_member:institution_members(id, full_name, first_name, last_name, profile_picture),
      region:regions(id, name, code),
      district:districts(id, name, code)
    `)
    .eq("institution_id", institutionId)
    .not("ended_at", "is", null)
    .order("ended_at", { ascending: false });
  if (error) throw error;
  return data as AuthorityWithPerson[];
}

// ── Minister search ───────────────────────────────────────────────────────────

export async function searchMinisters(query: string): Promise<MinisterSearchResult[]> {
  let q = supabase
    .from("ministers")
    .select("id, full_name, adb_code, profile_picture")
    .eq("is_active", true)
    .order("full_name")
    .limit(80);

  const tokens = query.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 1) {
    q = q.or(`adb_code.ilike.%${tokens[0]}%,full_name.ilike.%${tokens[0]}%`);
  } else if (tokens.length > 1) {
    tokens.forEach((t) => { q = q.ilike("full_name", `%${t}%`); });
  }

  const { data, error } = await q;
  if (error) throw error;
  return data as MinisterSearchResult[];
}

// ── Institution member search / create ───────────────────────────────────────

export async function searchInstitutionMembers(query: string): Promise<InstitutionMember[]> {
  let q = supabase
    .from("institution_members")
    .select("*")
    .eq("is_active", true)
    .eq("is_deleted", false)
    .order("full_name")
    .limit(80);

  const tokens = query.trim().split(/\s+/).filter(Boolean);
  if (tokens.length > 0) {
    tokens.forEach((t) => { q = q.ilike("full_name", `%${t}%`); });
  }

  const { data, error } = await q;
  if (error) throw error;
  return data;
}

type MemberFormData = Pick<
  InstitutionMemberInsert,
  "first_name" | "last_name" | "second_last_name" | "gender" | "dni" |
  "birth_date" | "email" | "cell_phone" | "district_id" | "region_id"
>;

export async function createInstitutionMember(
  data: MemberFormData,
  userId: string,
): Promise<InstitutionMember> {
  const { data: created, error } = await supabase
    .from("institution_members")
    .insert({ ...data, created_by: userId })
    .select()
    .single();
  if (error) throw error;
  return created;
}

export async function updateInstitutionMember(
  id: string,
  data: MemberFormData & { profile_picture_filename?: string | null },
  userId: string,
): Promise<InstitutionMember> {
  const { data: updated, error } = await supabase
    .from("institution_members")
    .update({ ...data, updated_at: new Date().toISOString(), updated_by: userId })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return updated;
}

export async function softDeleteInstitutionMember(
  id: string,
  userId: string,
): Promise<void> {
  // Also deactivate all memberships for this member
  await supabase
    .from("institution_memberships")
    .update({ is_active: false, updated_at: new Date().toISOString(), updated_by: userId })
    .eq("institution_member_id", id);

  const { error } = await supabase
    .from("institution_members")
    .update({ is_deleted: true, is_active: false, updated_at: new Date().toISOString(), updated_by: userId })
    .eq("id", id);
  if (error) throw error;
}

export async function uploadMemberPhoto(
  memberId: string,
  file: File,
): Promise<string> {
  const ext = file.name.split(".").pop() ?? "jpg";
  const filename = `InstitutionMemberPhotos/${memberId}.${ext}`;

  const { error } = await supabase.storage
    .from("MinisterImages")
    .upload(filename, file, { upsert: true, contentType: file.type });
  if (error) throw error;

  return filename;
}

export function getMemberPhotoUrl(filename: string | null): string | null {
  if (!filename) return null;
  const base = import.meta.env.VITE_SUPABASE_URL as string;
  return `${base}/storage/v1/object/public/MinisterImages/${filename}`;
}

// ── Authority mutations ───────────────────────────────────────────────────────

export async function assignAuthority(params: {
  institutionId: string;
  positionTypeId: string;
  scope: InstitutionScope;
  regionId?: string | null;
  districtId?: string | null;
  ministerId?: string | null;
  institutionMemberId?: string | null;
  startedAt: string;
  userId: string;
}): Promise<void> {
  const { institutionId, positionTypeId, scope, regionId, districtId, ministerId, institutionMemberId, startedAt, userId } = params;

  // End any existing active authority for this exact position slot
  const existing = await supabase
    .from("institution_authority_positions")
    .select("id")
    .eq("institution_id", institutionId)
    .eq("position_type_id", positionTypeId)
    .eq("scope", scope)
    .is("ended_at", null)
    .eq(regionId   ? "region_id"   : "institution_id", regionId   ?? institutionId)
    .eq(districtId ? "district_id" : "institution_id", districtId ?? institutionId);

  if (existing.data && existing.data.length > 0) {
    await supabase
      .from("institution_authority_positions")
      .update({ ended_at: startedAt, updated_at: new Date().toISOString(), updated_by: userId })
      .in("id", existing.data.map((r) => r.id));
  }

  const { error } = await supabase
    .from("institution_authority_positions")
    .insert({
      institution_id: institutionId,
      position_type_id: positionTypeId,
      scope,
      region_id: regionId ?? null,
      district_id: districtId ?? null,
      minister_id: ministerId ?? null,
      institution_member_id: institutionMemberId ?? null,
      started_at: startedAt,
      created_by: userId,
    });
  if (error) throw error;
}

export async function endAuthority(
  authorityId: string,
  endedAt: string,
  userId: string,
): Promise<void> {
  const { error } = await supabase
    .from("institution_authority_positions")
    .update({ ended_at: endedAt, updated_at: new Date().toISOString(), updated_by: userId })
    .eq("id", authorityId);
  if (error) throw error;
}

// ── Membership mutations ──────────────────────────────────────────────────────

export async function addMembership(params: {
  institutionId: string;
  ministerId?: string | null;
  institutionMemberId?: string | null;
  userId: string;
}): Promise<void> {
  const { error } = await supabase
    .from("institution_memberships")
    .insert({
      institution_id: params.institutionId,
      minister_id: params.ministerId ?? null,
      institution_member_id: params.institutionMemberId ?? null,
      joined_at: new Date().toISOString().slice(0, 10),
      created_by: params.userId,
    });
  if (error) throw error;
}

export async function removeMembership(
  membershipId: string,
  leftAt: string,
  notes: string | null,
  userId: string,
): Promise<void> {
  const { error } = await supabase
    .from("institution_memberships")
    .update({
      is_active: false,
      left_at: leftAt,
      notes,
      updated_at: new Date().toISOString(),
      updated_by: userId,
    })
    .eq("id", membershipId);
  if (error) throw error;
}
